import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useGameState } from '../hooks/useGameState';
import { useTimer } from '../hooks/useTimer';
import { useMatchmaking } from '../hooks/useMatchmaking';
import { getIdToken } from '../services/authService';
import { GameBoard } from '../components/GameBoard';
import { ScoreHeader } from '../components/ScoreHeader';
import { TurnIndicator } from '../components/TurnIndicator';
import { TimerDisplay } from '../components/TimerDisplay';
import { GameStartOverlay } from '../components/GameStartOverlay';
import { ResignButton } from '../components/ResignButton';
import { MatchmakingOverlay } from '../components/MatchmakingOverlay';
import { ReconnectingBanner } from '../components/ReconnectingBanner';
import type {
  BoardUpdatePayload,
  GameStartPayload,
} from '../hooks/useWebSocket';
import styles from './GamePage.module.css';

// ─── Types ──────────────────────────────────────────────────────────────────

type GamePhase = 'matchmaking' | 'starting' | 'playing' | 'ended';

export interface GamePageProps {
  onExit: () => void;
}

// ─── Inline result banner (sits above the board; board stays visible) ─────────

const RESULT_TEXT: Record<string, string> = {
  win: 'You Win! 🍄',
  lose: 'You Lost',
  draw: 'Draw!',
  forfeit: 'Opponent Forfeited!',
  resign: 'Opponent Resigned!',
};

function ResultBanner({ result }: { result: string }) {
  return (
    <div className={styles.resultBanner} role="status" aria-live="assertive">
      <span className={`${styles.resultText} ${styles[`result_${result}`] ?? ''}`}>
        {RESULT_TEXT[result] ?? 'Game Over'}
      </span>
    </div>
  );
}

// ─── Constants ──────────────────────────────────────────────────────────────

const API_BASE_URL = '/api';
const WS_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WS_URL) ||
  'wss://ws.xox.pragmaticmushroom.com';

// ─── Component ──────────────────────────────────────────────────────────────

export function GamePage({ onExit }: GamePageProps) {
  const [phase, setPhase] = useState<GamePhase>('matchmaking');
  const [youGoFirst, setYouGoFirst] = useState(false);
  const [gameId, setGameId] = useState<string | null>(null);
  const [myNickname] = useState('You');

  // myAvatar comes from the game_start payload via useGameState
  const phaseRef = useRef<GamePhase>('matchmaking');

  // Keep ref in sync for use in callbacks
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // ─── Auth helper ────────────────────────────────────────────────────────

  const getAuthToken = useCallback(async (): Promise<string> => {
    // Cognito User Pools authorizer validates the ID token (has the aud claim)
    const token = getIdToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    return token;
  }, []);

  // ─── Hooks ──────────────────────────────────────────────────────────────

  const {
    connect,
    disconnect,
    sendMessage,
    lastMessage,
    isConnected,
    isReconnecting,
  } = useWebSocket({
    apiBaseUrl: API_BASE_URL,
    wsBaseUrl: WS_BASE_URL,
    getAuthToken,
  });

  const {
    board,
    scores,
    gameResult,
    isMyTurn,
    myAvatar: actualAvatar,
    opponentInfo,
    winningCells,
    opponentLeft,
    opponentLeftReason,
    error,
    processMessage,
    applyOptimisticMove,
    resetGame,
    resetSeries,
  } = useGameState();

  const {
    secondsRemaining,
    isUrgent,
    startTimer,
    stopTimer,
    resetTimer,
  } = useTimer();

  const {
    joinQueue,
    cancelQueue,
    isSearching,
    elapsedTime,
    matchResult,
  } = useMatchmaking({
    apiBaseUrl: API_BASE_URL,
    getAuthToken,
    lastMessage,
  });

  // ─── Process incoming WebSocket messages ────────────────────────────────

  useEffect(() => {
    if (!lastMessage) return;

    processMessage(lastMessage);

    // Handle specific events for phase and timer management
    switch (lastMessage.event) {
      case 'game_start': {
        const payload = lastMessage.payload as GameStartPayload | undefined;
        if (!payload) break;
        setGameId(payload.gameId);
        setYouGoFirst(payload.youGoFirst);
        setPhase('starting');
        resetTimer();
        break;
      }

      case 'board_update': {
        const payload = lastMessage.payload as BoardUpdatePayload;
        // Timer runs only during the local player's turn.
        // startTimer expects an ISO timestamp (parsed via new Date()).
        if (payload.isYourTurn) {
          startTimer(new Date().toISOString());
        } else {
          stopTimer();
        }
        break;
      }

      case 'game_end': {
        stopTimer();
        setPhase('ended');
        break;
      }

      case 'opponent_left': {
        // Opponent disconnected — timer continues; server will award win on forfeit
        break;
      }

      default:
        break;
    }
  }, [lastMessage, processMessage, startTimer, stopTimer, resetTimer]);

  // ─── Matchmaking flow: connect WS → join queue ─────────────────────────

  useEffect(() => {
    connect();
    // Start matchmaking once the WebSocket connects
    // (handled in the isConnected effect below)
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Join the queue once WebSocket is connected and we're in matchmaking phase
  const hasJoinedRef = useRef(false);
  useEffect(() => {
    if (isConnected && phase === 'matchmaking' && !hasJoinedRef.current && !isSearching) {
      hasJoinedRef.current = true;
      joinQueue().catch(() => {
        // If matchmaking join fails, allow retry
        hasJoinedRef.current = false;
      });
    }
  }, [isConnected, phase, isSearching, joinQueue]);

  // When matchResult arrives via the matchmaking hook, transition to starting phase.
  // The HTTP join response also carries the initial board + isYourTurn, so seed
  // game state from it (covers the bot case where no game_start WS event arrives).
  useEffect(() => {
    if (matchResult) {
      setGameId(matchResult.gameId);
      setYouGoFirst(matchResult.youGoFirst);
      processMessage({ event: 'game_start', payload: matchResult as never });
      setPhase('starting');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchResult]);

  // ─── Game actions ───────────────────────────────────────────────────────

  const handleCellClick = useCallback(
    (cell: number) => {
      if (!isMyTurn || phase !== 'playing' || gameResult || !gameId) return;
      if (board[cell] !== null) return; // cell already occupied
      // Optimistically show the player's mushroom immediately; the server
      // board_update will reconcile (and correct it if the move is rejected).
      applyOptimisticMove(cell);
      sendMessage('move', { cell, game_id: gameId });
    },
    [isMyTurn, phase, gameResult, gameId, board, applyOptimisticMove, sendMessage],
  );

  const handleResign = useCallback(() => {
    if (!gameId) return;
    sendMessage('resign', { game_id: gameId });
  }, [gameId, sendMessage],
  );

  const handlePlayAgain = useCallback(() => {
    resetGame();
    resetTimer();
    setPhase('matchmaking');
    hasJoinedRef.current = false;
    sendMessage('play_again');
  }, [resetGame, resetTimer, sendMessage]);

  const handleExit = useCallback(() => {
    sendMessage('exit');
    disconnect();
    resetSeries();
    onExit();
  }, [sendMessage, disconnect, resetSeries, onExit]);

  const handleCancelMatchmaking = useCallback(async () => {
    try {
      await cancelQueue();
    } catch {
      // Ignore cancel errors
    }
    disconnect();
    resetSeries();
    onExit();
  }, [cancelQueue, disconnect, resetSeries, onExit]);

  const handleStartOverlayDismiss = useCallback(() => {
    setPhase('playing');
    // Start the countdown if it's already the player's turn at game start
    if (isMyTurn) {
      startTimer(new Date().toISOString());
    }
  }, [isMyTurn, startTimer]);

  // ─── Derived state ─────────────────────────────────────────────────────

  const isGameActive = phase === 'playing' && !gameResult;
  const showBoard = phase === 'playing' || phase === 'ended' || phase === 'starting';

  // Local player's avatar — from game_start payload, with a safe fallback
  const myAvatar = actualAvatar ?? 'Fly Agaric';

  // Build ScoreHeader player info — scores are {you, opponent} from our POV
  const player1Info = {
    nickname: myNickname,
    avatar: myAvatar,
    score: scores.you,
  };

  const player2Info = opponentInfo
    ? {
        nickname: opponentInfo.nickname,
        avatar: opponentInfo.avatar,
        score: scores.opponent,
      }
    : {
        nickname: 'Opponent',
        avatar: 'chanterelle',
        score: 0,
      };

  // Turn indicator info
  const turnNickname = isMyTurn ? myNickname : (opponentInfo?.nickname ?? 'Opponent');
  const turnAvatar = isMyTurn ? myAvatar : (opponentInfo?.avatar ?? 'chanterelle');

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className={styles.gamePage}>
      {/* Reconnecting banner */}
      {isReconnecting && <ReconnectingBanner />}

      {/* Matchmaking overlay */}
      {phase === 'matchmaking' && isSearching && (
        <div className={styles.overlayContainer}>
          <MatchmakingOverlay
            elapsedTime={elapsedTime}
            onCancel={handleCancelMatchmaking}
          />
        </div>
      )}

      {/* Game start overlay */}
      {phase === 'starting' && opponentInfo && (
        <div className={styles.overlayContainer}>
          <GameStartOverlay
            youGoFirst={youGoFirst}
            opponentNickname={opponentInfo.nickname}
            opponentAvatar={opponentInfo.avatar}
            onDismiss={handleStartOverlayDismiss}
          />
        </div>
      )}

      {/* Main game UI */}
      {showBoard && (
        <>
          <header className={styles.gameHeader}>
            <ScoreHeader
              player1={player1Info}
              player2={player2Info}
              sameAvatar={myAvatar === opponentInfo?.avatar}
            />
          </header>

          <div className={styles.gameBody}>
            <div className={styles.turnTimerRow}>
              {phase === 'playing' && (
                <TurnIndicator
                  nickname={turnNickname}
                  avatar={turnAvatar}
                  isMyTurn={isMyTurn}
                />
              )}
              {phase === 'playing' && (
                <TimerDisplay
                  seconds={secondsRemaining}
                  isUrgent={isUrgent}
                  isMyTurn={isMyTurn}
                />
              )}
              {phase === 'ended' && gameResult && (
                <ResultBanner result={gameResult.result} />
              )}
            </div>

            <div className={styles.boardSection}>
              <GameBoard
                board={board}
                onCellClick={handleCellClick}
                isMyTurn={isMyTurn}
                winningCells={winningCells ?? []}
                disabled={!isGameActive}
              />
            </div>
          </div>

          {/* Opponent disconnect message */}
          {opponentLeft && !gameResult && (
            <div className={styles.opponentDisconnected} role="alert">
              Opponent disconnected
              {opponentLeftReason ? ` — ${opponentLeftReason}` : ''}
              . Waiting for timer to expire...
            </div>
          )}

          {/* Footer: resign while playing, result actions when ended */}
          <div className={styles.footer}>
            {phase === 'ended' && gameResult ? (
              <div className={styles.resultButtons}>
                <button
                  type="button"
                  className={`${styles.resultButton} ${styles.playAgainButton}`}
                  onClick={handlePlayAgain}
                >
                  Play Again
                </button>
                <button
                  type="button"
                  className={`${styles.resultButton} ${styles.exitButton}`}
                  onClick={handleExit}
                >
                  Exit
                </button>
              </div>
            ) : (
              <ResignButton
                onResign={handleResign}
                disabled={!isGameActive || !!gameResult}
              />
            )}
          </div>

          {/* Error display */}
          {error && (
            <div role="alert" aria-live="polite" className={styles.opponentDisconnected}>
              {error.message}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default GamePage;
